import React, { useState, useEffect } from "react";
import debounce from "lodash.debounce";
import { useSelector } from "react-redux";
import { Formik, Form, useFormikContext } from "formik";
import isEmpty from "lodash.isempty";
import * as Yup from "yup";
import Skeleton from "react-loading-skeleton";
import { TextInput } from "../FormInputs/TextInput";
import { ImageUpload } from "../FormInputs/ImageUpload";
import { ResourceProjectForm } from "../FormInputs/ResourceProjects/ResourceProjectForm";
import { Sectors } from "../FormInputs/Sectors";
import { Modal } from "../Modal";
import { OnSubmitValidationError } from "../../utils/OnSubmitValidationError";
import { ResumeForm } from "../Resume/ResumeForm";
import { TabHeader } from "../TabHeader";
import { FlashError } from "../FormInputs/FlashError";
import { Spinner } from "@chakra-ui/react";
import { Spacer } from "@chakra-ui/react";
import { ResourceProjects } from "components/FormInputs/ResourceProjects";
import { Resume } from "components/Resume/Resume";
import { Button } from "components/RFQ/Button";
import { SmartFieldNote } from "components/RFQ/SmartFieldEditor/_components/SmartFieldNote";
import { PROFILE_TYPE_CONSULTANT } from "utils/constants";
import { ExpertiseRepeater } from "components/FormInputs/ExpertiseRepeater";
import {
	extractResumeFileId,
	normalizeDefaultValue,
	processResumeFilesForSubmission,
	initResumeMetadataObjects,
	shouldBeDefaultFile,
	getNonRemovedIndices,
	getResumeFileId,
	reindexResumeMetadataObjects,
} from "utils/resumeHelpers";

const AutoSave = ({ debounceMs, setAddResume }) => {
	const formik = useFormikContext();
	formik.values.status = "published";
	// eslint-disable-next-line
	const debouncedSubmit = React.useCallback(
		debounce(() => formik.submitForm(), debounceMs),
		[formik.submitForm]
	);

	useEffect(() => {
		debouncedSubmit();
		setAddResume(false);
	}, [debouncedSubmit, setAddResume, formik.values]);

	return true;
};

export const ResourceForm = ({
	initialValues,
	projectsDataStructure,
	editForm,
	autoUpdateForm,
	handleDelete,
	setAutoUpdateForm,
	isRequesting,
	customButtons,
	hideDefaultButtons = false,
	supplierSmartField = false,
	innerRef,
	...props
}) => {
	const [modalIsOpen, setModalIsOpen] = useState(false);
	const [resumeModalIsOpen, setResumeModalIsOpen] = useState(false);
	const [editId, setEditId] = useState(false);
	const [editResumeId, setEditResumeId] = useState(false);
	const [addResume, setAddResume] = useState(false);

	const { current_company } = useSelector((state) => state.auth);
	const profileType = current_company.profile_type;

	const validationSchema = Yup.object({
		first_name: Yup.string().required(),
		last_name: Yup.string().required(),
		email: Yup.string().email().required(),
		position: Yup.string().required(),
		experience: Yup.number()
			.required()
			.min(1, "At least one year of experience is required")
			.max(100, "No on can have experience more that 100 years"),
		roles: Yup.string().required(),
		sector_ids: Yup.array().min(1, "You must select at least one sector"),
		disciplines:
			profileType === PROFILE_TYPE_CONSULTANT
				? Yup.array().of(
						Yup.object().shape({
							expertise: Yup.string().required("Expertise is required"),
							disciplines: Yup.array().min(
								1,
								"You must select at least one discipline"
							),
						})
					)
				: Yup.array(),
	});

	return initialValues !== undefined ? (
		<Formik
			enableReinitialize={editForm}
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={(values, formikBag) => {
				// process metadata for resume files
				if (values.resume_files && values.resume_files.length > 0) {
					// handle _isFile wrapper objects if needed
					const processedResumeFiles = values.resume_files.map((file) => {
						//  object, return the actual File // type guard
						if (file && file._isFile && file._file) {
							return file._file;
						}
						return file;
					});

					values.resume_files = processedResumeFiles;

					// for submission
					const processedValues = processResumeFilesForSubmission(values);

					props.handleSubmit(processedValues, formikBag);
					return;
				}
				// Submit form with original values if no resume files
				props.handleSubmit(values, formikBag);
			}}
			innerRef={innerRef}
		>
			{(props) => {
				const {
					setFieldValue,
					values,
					isSubmitting,
					submitForm,
					status,
					setStatus,
				} = props;

				const handleSubmitProject = (projectValues) => {
					//using an internal state value here to track which index of the projects_completed array we're editing
					//if editId is set, update the value in the form values array matching the editId
					if (editId !== false) {
						values.projects_completed.splice(editId, 1, projectValues);
					} else {
						//else if nothing is set, we'll assume that we're creating a new project and update the whole array.
						//need to add to the current projects_completed array and replace the whole form value with the new array
						values.projects_completed.push(projectValues);
					}
					setModalIsOpen(false);
				};

				const handleSubmitResume = (resumeValues) => {
					// Extract resume file and default status
					const resumeFile = resumeValues.resume || resumeValues.resume_files;
					let isDefault = resumeValues.is_default || "0";

					// Normalize is_default to "0"/"1"
					isDefault = normalizeDefaultValue(isDefault);

					if (!resumeFile) {
						setResumeModalIsOpen(false);
						return;
					}

					// Check if file is a direct File object
					const isActualFile = resumeFile instanceof File;

					// Initialize metadata objects if needed
					initResumeMetadataObjects(values);

					if (editResumeId !== false) {
						// Update existing resume
						if (!Array.isArray(values.resume_files)) {
							values.resume_files = [];
						}

						const updatedResumes = [...values.resume_files];

						// Store the file itself in the resume_files array
						if (
							updatedResumes[editResumeId] &&
							updatedResumes[editResumeId]._isFile
						) {
							// replace the entire wrapper object
							updatedResumes[editResumeId] = {
								_isFile: true,
								_file: resumeFile,
								_originalIndex: editResumeId,
							};
						} else {
							updatedResumes[editResumeId] = resumeFile;
						}

						// File is default if it's the only one or explicitly marked as default
						const shouldBeDefault = shouldBeDefaultFile(
							updatedResumes.length,
							isDefault
						);

						// store metadata separately
						values.resume_files_is_default[editResumeId] = shouldBeDefault
							? "1"
							: "0";

						// ID handling - keep existing ID or set to null for new file
						if (isActualFile) {
							// For new file upload, set id to null // backend will set once it's saved
							values.resume_files_id[editResumeId] = null;
						} else {
							// For existing file, extract ID
							values.resume_files_id[editResumeId] =
								getResumeFileId(resumeFile);
						}

						// reset is_remove flag when editing
						values.resume_files_is_remove[editResumeId] = "0";

						// Find all non-removed files
						const nonRemovedIndices = getNonRemovedIndices(
							updatedResumes,
							values.resume_files_is_remove
						);

						// If this file should be default, make sure all others are not default
						if (shouldBeDefault) {
							const updatedIsDefault = { ...values.resume_files_is_default };

							// Set all existing to "0" first
							nonRemovedIndices.forEach((idx) => {
								if (idx !== editResumeId) {
									updatedIsDefault[idx] = "0";
								}
							});

							// Make sure this file is default
							updatedIsDefault[editResumeId] = "1";

							values.resume_files_is_default = updatedIsDefault;
						}
						// Ensure one file is set as default when there are multiple files
						else if (
							!nonRemovedIndices.some(
								(idx) => values.resume_files_is_default[idx] === "1"
							) &&
							nonRemovedIndices.length > 0
						) {
							// If no file is currently set as default, set the first non-removed as default
							const firstNonRemoved = nonRemovedIndices[0];
							values.resume_files_is_default[firstNonRemoved] = "1";
						}

						setFieldValue("resume_files", updatedResumes);
						setFieldValue(
							"resume_files_is_default",
							values.resume_files_is_default
						);
						setFieldValue("resume_files_id", values.resume_files_id);
						setFieldValue(
							"resume_files_is_remove",
							values.resume_files_is_remove
						);
					} else {
						// Add new resume to the array
						let newResumes = Array.isArray(values.resume_files)
							? [...values.resume_files]
							: [];
						const newIndex = newResumes.length;

						// just add the file directly without extra properties
						// If it's a File object, use our wrapper
						if (resumeFile instanceof File) {
							newResumes.push({
								_isFile: true,
								_file: resumeFile,
								_originalIndex: newIndex,
							});
						} else {
							newResumes.push(resumeFile);
						}

						// File is default if it's the only one or explicitly marked as default
						const shouldBeDefault = shouldBeDefaultFile(
							newResumes.length,
							isDefault
						);

						// Store metadata separately
						values.resume_files_is_default[newIndex] = shouldBeDefault
							? "1"
							: "0";

						// Get ID for new file
						values.resume_files_id[newIndex] = getResumeFileId(resumeFile);

						values.resume_files_is_remove[newIndex] = "0"; // Not removing

						// Find all non-removed files
						const nonRemovedIndices = getNonRemovedIndices(
							newResumes,
							values.resume_files_is_remove
						);

						// If this file should be default, make sure all others are not default
						if (shouldBeDefault) {
							const updatedIsDefault = {
								...values.resume_files_is_default,
							};

							// Set all existing to "0" first
							nonRemovedIndices.forEach((idx) => {
								if (idx !== newIndex) {
									updatedIsDefault[idx] = "0";
								}
							});

							// Make sure this file is default
							updatedIsDefault[newIndex] = "1";

							values.resume_files_is_default = updatedIsDefault;
						}
						// If no non-removed file is set as default, set this new file as default
						else if (
							!nonRemovedIndices.some(
								(idx) => values.resume_files_is_default[idx] === "1"
							)
						) {
							values.resume_files_is_default[newIndex] = "1";
						}

						setFieldValue("resume_files", newResumes);
						setFieldValue(
							"resume_files_is_default",
							values.resume_files_is_default
						);
						setFieldValue("resume_files_id", values.resume_files_id);
						setFieldValue(
							"resume_files_is_remove",
							values.resume_files_is_remove
						);
					}

					setAddResume(true);
					setResumeModalIsOpen(false);
				};

				const handleEditProject = (index) => {
					//use this internal state to track of the array index of project currently being edited
					setEditId(index);
					setModalIsOpen(true);
				};

				const handleEditResume = (index) => {
					setEditResumeId(index);
					setResumeModalIsOpen(true);
				};

				const handleDeleteProject = (index) => {
					//remove element from values array
					values.projects_completed.splice(index, 1);
					//use the formik provided function here so the form state updates properly
					setFieldValue("projects_completed", values.projects_completed);
				};

				const handleDeleteResume = (index) => {
					// For files with existing IDs, mark for removal with is_remove flag instead of deleting
					// Extract ID from file object
					const fileId = extractResumeFileId(values.resume_files[index]);

					// Check if the file being deleted was the default
					const wasDefault = values.resume_files_is_default?.[index] === "1";
					if (fileId) {
						// For existing files (with ID) - set is_remove flag

						// Initialize metadata objects if needed
						if (!values.resume_files_is_remove) {
							values.resume_files_is_remove = {};
						}

						if (!values.resume_files_id) {
							values.resume_files_id = {};
						}

						// Store ID and set is_remove flag
						values.resume_files_id[index] = fileId;
						values.resume_files_is_remove[index] = "1";

						// Always clear the default flag for removed files
						if (values.resume_files_is_default?.[index] === "1") {
							values.resume_files_is_default[index] = "0";
						}

						// Mark as visually removed in UI
						const updatedResumes = [...values.resume_files];
						updatedResumes[index] = {
							...updatedResumes[index],
							is_remove: "1",
							_ui_removed: true, // For UI filtering
							is_default: "0", // Ensure removed files are not default
						};

						// Apply the updates to the form
						setFieldValue("resume_files", updatedResumes);
						setFieldValue("resume_files_id", values.resume_files_id);
						setFieldValue(
							"resume_files_is_remove",
							values.resume_files_is_remove
						);

						// Find all non-removed files
						const nonRemovedIndices = getNonRemovedIndices(
							updatedResumes,
							values.resume_files_is_remove
						);
						// If we have non-removed files and the current file was default or no file is default
						if (
							nonRemovedIndices.length > 0 &&
							(wasDefault ||
								!Object.entries(values.resume_files_is_default || {}).some(
									([idx, value]) =>
										value === "1" && !nonRemovedIndices.includes(Number(idx))
								))
						) {
							const updatedIsDefault = {
								...values.resume_files_is_default,
							};
							// Set all to "0" first
							Object.keys(updatedIsDefault).forEach((key) => {
								updatedIsDefault[key] = "0";
							});

							// Set the first non-removed as default
							updatedIsDefault[nonRemovedIndices[0]] = "1";
							setFieldValue("resume_files_is_default", updatedIsDefault);
						}
					} else {
						// For new files (without ID) - remove completely from array
						const updatedResumes = [...values.resume_files];
						updatedResumes.splice(index, 1);

						// Update metadata objects
						const updatedIsDefault = { ...values.resume_files_is_default };
						const updatedIds = { ...values.resume_files_id };
						const updatedIsRemove = { ...values.resume_files_is_remove };

						// Reindex remaining items
						reindexResumeMetadataObjects(
							index,
							updatedResumes,
							updatedIsDefault,
							updatedIds,
							updatedIsRemove
						);

						// If we just deleted the default file and have remaining files, make the first one default
						if (wasDefault && updatedResumes.length > 0) {
							// Set all to "0" first
							Object.keys(updatedIsDefault).forEach((key) => {
								updatedIsDefault[key] = "0";
							});
							// First file becomes default
							updatedIsDefault[0] = "1";
						}

						// If only one file remains, make it the default
						if (updatedResumes.length === 1) {
							updatedIsDefault[0] = "1";
						}

						setFieldValue("resume_files", updatedResumes);
						setFieldValue("resume_files_is_default", updatedIsDefault);
						setFieldValue("resume_files_id", updatedIds);
						setFieldValue("resume_files_is_remove", updatedIsRemove);
					}

					setAddResume(true);
				};

				const onSubmitValidationError = () => {};
				return (
					<>
						{!supplierSmartField ? (
							<TabHeader
								heading={
									supplierSmartField || editForm
										? `Edit ${initialValues.first_name} ${initialValues.last_name}`
										: "Create Key Personnel"
								}
							/>
						) : null}

						<Form onChange={() => setStatus(null)}>
							{editForm &&
								status === null &&
								!autoUpdateForm &&
								isEmpty(status) &&
								!(values.status === "published" && isSubmitting) &&
								(JSON.stringify(initialValues) !== JSON.stringify(values) ||
									addResume) && (
									<AutoSave
										debounceMs={1000}
										setAddResume={setAddResume}
									/>
								)}

							{supplierSmartField && (
								<SmartFieldNote
									type="key personnel"
									className="-mt-6 mb-8"
								/>
							)}

							<div
								id="wrapper__primary-address"
								spacing={2}
								align="stretch"
							>
								<div className="flex flex-col gap-x-6 lg:grid lg:grid-cols-2 lg:gap-y-2">
									<ImageUpload
										label="Profile Photo"
										name="avatar_image"
										image={initialValues.avatar_image}
										setFieldValue={setFieldValue}
										module="key-personel"
										className="mb-0 pb-0"
										isFullHeight
									/>
									<Spacer className="hidden lg:block" />
									<TextInput
										label="First Name"
										required="true"
										name="first_name"
										type="text"
									/>
									<TextInput
										label="Last Name"
										required="true"
										name="last_name"
										type="text"
									/>
									<TextInput
										label="Email"
										required="true"
										name="email"
										type="email"
									/>

									<TextInput
										label="Current Position"
										required="true"
										name="position"
										type="text"
									/>
									<TextInput
										label="Industry Experience in Years"
										required="true"
										name="experience"
										type="number"
										max="50"
										min="1"
										guideLine="Will be updated every year"
									/>

									<Sectors
										label="Top 3 Sectors"
										required="true"
										name="sector_ids"
										setFieldValue={setFieldValue}
										values={values}
										maxOptions={3}
										testId="select-sectors"
									/>
									{profileType?.id === PROFILE_TYPE_CONSULTANT && (
										<div className="col-span-2">
											<ExpertiseRepeater
												testId="project-expertise-discipline"
												name="disciplines"
												values={values.disciplines}
												profileType={profileType}
												required
												discription="Specify your role or the expertise you provide for your company."
											/>
										</div>
									)}
									<ResourceProjects
										projects={values.projects_completed}
										setFieldValue={setFieldValue}
										className="col md-12"
										handleAddClick={() => {
											setEditId(false);
											setModalIsOpen(true);
										}}
										handleEditClick={handleEditProject}
										handleDeleteClick={handleDeleteProject}
									/>
									<Resume
										resume={values.resume_files}
										setFieldValue={setFieldValue}
										className="col md-12"
										handleAddClick={() => {
											setEditResumeId(false);
											setResumeModalIsOpen(true);
										}}
										handleEditClick={handleEditResume}
										handleDeleteClick={handleDeleteResume}
									/>
								</div>
							</div>
							<TextInput
								name="company_id"
								value={values.company_id}
								type="hidden"
							/>
							<div className="col xl-12">
								{status && (
									<FlashError
										heading={errorMessages[status]?.heading || status}
										text={errorMessages[status]?.description || status}
										margin="0px 0px 20px 0px"
									/>
								)}
							</div>

							{supplierSmartField && (
								<SmartFieldNote
									type="key personnel"
									className="mb-8 mt-4"
								/>
							)}

							{customButtons ||
								(!hideDefaultButtons && (
									<div className="mt-6 flex gap-8">
										<Button
											type="button"
											isSubmitting={
												values.status === "published" && isSubmitting
											}
											text="Save to Profile"
											disablePointer={isSubmitting}
											disabledStyle={isSubmitting}
											onClick={() => {
												setFieldValue("status", "published", false);
												editForm && setAutoUpdateForm(true);
												submitForm();
											}}
											data-testid="submit-resource"
										>
											{values.status === "published" && isSubmitting ? (
												<Spinner className="spinner" />
											) : (
												"Save to Profile"
											)}
										</Button>

										{handleDelete && (
											<Button
												type="button"
												danger
												onClick={handleDelete}
												disablePointer={isRequesting}
												disabledStyle={isRequesting}
												data-testid="delete-resource"
											>
												{!isRequesting ? "Delete Personnel" : "Deleting..."}
											</Button>
										)}
									</div>
								))}

							<OnSubmitValidationError callback={onSubmitValidationError} />
						</Form>

						<Modal
							isOpen={modalIsOpen}
							onRequestClose={() => setModalIsOpen(false)}
						>
							<ResourceProjectForm
								index={editId}
								title="Project Details"
								values={values}
								onSubmit={handleSubmitProject}
								initialValues={
									editId !== false
										? values.projects_completed[editId]
										: {
												name: "",
												sector_id: "",
												role: "",
												location: "",
												value: "",
											}
								}
								isEdit={editId !== false}
							/>
						</Modal>
						<Modal
							isOpen={resumeModalIsOpen}
							onRequestClose={() => setResumeModalIsOpen(false)}
						>
							<ResumeForm
								values={values}
								onSubmit={handleSubmitResume}
								initialValues={
									editResumeId !== false
										? {
												resume:
													values.resume_files[editResumeId]?.resume_file || {},
											}
										: { resume: {} }
								}
								testId="upload-resume"
							/>
						</Modal>
					</>
				);
			}}
		</Formik>
	) : (
		<Skeleton
			count={5}
			duration={0.5}
		/>
	);
};
const errorMessages = {
	required: {
		heading: "* Required Field",
		description: "There are incomplete required fields. Please complete them.",
	},
	connection: {
		heading: "Connection not established",
		description: "Unable to process your request",
	},
	"Email Already Exist": {
		heading: "User Already Exist !",
		description: "Please, try with different email address.",
	},
};
